import { locale, t } from '../i18n';

export function formatDistance(m: number): { value: string; unit: string } {
  if (m < 1000) return { value: Math.round(m).toString(), unit: 'm' };
  return { value: (m / 1000).toFixed(m < 10_000 ? 2 : 1), unit: 'km' };
}

export function formatKm(m: number, digits = 2): string {
  return (m / 1000).toFixed(digits);
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  const ss = String(sec).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Pace as m:ss per km. */
export function formatPace(secPerKm: number | null | undefined): string {
  if (!secPerKm || !Number.isFinite(secPerKm) || secPerKm > 3600) return '--:--';
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return s === 60 ? `${m + 1}:00` : `${m}:${String(s).padStart(2, '0')}`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale(), { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(locale(), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function timeAgo(iso: string, now = Date.now()): string {
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  if (s < 45) return t('just now');
  if (s < 3600) return t('{n}m ago', { n: Math.round(s / 60) });
  if (s < 86_400) return t('{n}h ago', { n: Math.round(s / 3600) });
  return t('{n}d ago', { n: Math.round(s / 86_400) });
}

/** "Morning Run", "Evening Run"… from the local start hour, like Strava. */
export function runTitle(iso: string): string {
  const h = new Date(iso).getHours();
  if (h >= 4 && h < 11) return t('Morning Run');
  if (h >= 11 && h < 14) return t('Lunch Run');
  if (h >= 14 && h < 18) return t('Afternoon Run');
  if (h >= 18 && h < 22) return t('Evening Run');
  return t('Night Run');
}

/** "Today at 4:50 AM", "Yesterday at 6:10 PM", "Mon, 5 Oct at 7:02 AM". */
export function formatRunWhen(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(locale(), { hour: 'numeric', minute: '2-digit' });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return t('Today at {time}', { time });
  if (diff === 1) return t('Yesterday at {time}', { time });
  return t('{date} at {time}', { date: d.toLocaleDateString(locale(), { weekday: 'short', day: 'numeric', month: 'short' }), time });
}
